'use client';

import { useState } from 'react';

import { shareUrlForTarget } from '@ps/features/sharing/shareTarget';
import { useShareDialog } from '@ps/features/sharing/useShareDialog';
import { config } from '@ps/functions/config';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@ps/ui/atoms/dropdown-menu';
import { Bell, BellOff, Info, MoreVertical, Share2 } from 'lucide-react';

import { useCommunityContext } from '../../contexts/community/useCommunityContext';
import { useChannelNotificationSettings } from '../../hooks/useChannelNotificationSettings';
import { useMyChannelMembership } from '../../hooks/useMyChannelMembership';
import type { CommunityChannel } from '../../types/CommunityChannel';
import { ChannelInfoDialog } from '../channel-info/ChannelInfoDialog';
import { ChannelInfoPopover } from '../channel-info/ChannelInfoPopover';

interface Props {
  channel: CommunityChannel;
}

/**
 * The always-visible channel header kebab — the web port of PopshopRN's
 * `CommunityChannelMenu`. Everyone (owner + visitor) gets "Channel Info"; a
 * dev/staging-only "Copy Reference" copies the channel's Firestore path for
 * internal tooling. Where management is enabled (the dashboard), "Channel Info"
 * opens the admin console ({@link ChannelInfoPopover}, with edit/delete); on the
 * visitor surface — including an owner previewing their own bio — it opens the
 * read-only {@link ChannelInfoDialog}.
 */
export function ChannelHeaderMenu({ channel }: Props) {
  const { canManage, creatorId, bioContext } = useCommunityContext();
  const { openShare } = useShareDialog();
  const [infoOpen, setInfoOpen] = useState(false);

  // Channel-level mute toggle, gated to active members (a membership doc =
  // joined). `subscribed` = notifications on; the item then offers "Mute", and
  // when muted it offers "Notifications" to turn them back on — matching
  // upstream's `CommunityChannelMenu`.
  const member = useMyChannelMembership(channel.id);
  const { setMuted } = useChannelNotificationSettings({
    channelId: channel.id,
  });
  const isMember = member != null;
  const muted = member?.muted ?? false;

  // Dev/staging only — the copied path is for internal routing tools, matching
  // upstream's `!config.isProd || __DEV__` gate.
  const showCopyReference = config.env !== 'prod';

  const shareChannel = () => {
    openShare({
      url: shareUrlForTarget(bioContext, {
        kind: 'community-channel',
        slug: channel.slug,
      }),
      title: channel.name,
    });
  };

  const copyReference = async () => {
    const ref = ['stores', creatorId, 'communityChannels', channel.id].join(
      '/',
    );
    try {
      await navigator.clipboard.writeText(ref);
    } catch (error: unknown) {
      // Clipboard can reject in insecure contexts — nothing actionable, but
      // surface it rather than swallow silently.
      console.error('Failed to copy channel reference:', error);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Channel actions"
            className="hover:text-content-1 text-content-2"
          >
            <MoreVertical className="h-5 w-5" aria-hidden />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => setInfoOpen(true)}>
            <Info className="h-4 w-4" aria-hidden />
            Channel Info
          </DropdownMenuItem>
          {isMember ? (
            <DropdownMenuItem
              onSelect={() => void setMuted(!muted)}
              className={muted ? undefined : 'text-danger focus:text-danger'}
            >
              {muted ? (
                <>
                  <Bell className="h-4 w-4" aria-hidden />
                  Notifications
                </>
              ) : (
                <>
                  <BellOff className="h-4 w-4" aria-hidden />
                  Mute Notifications
                </>
              )}
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem onSelect={shareChannel}>
            <Share2 className="h-4 w-4" aria-hidden />
            Share
          </DropdownMenuItem>
          {showCopyReference ? (
            <DropdownMenuItem onSelect={copyReference}>
              {/* Spacer matching the icon column so this item's text
                  left-aligns with the icon'd items above. */}
              <span className="h-4 w-4 shrink-0" aria-hidden />
              Copy Reference
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      {canManage ? (
        <ChannelInfoPopover
          channel={channel}
          open={infoOpen}
          onOpenChange={setInfoOpen}
        />
      ) : (
        <ChannelInfoDialog
          channel={channel}
          open={infoOpen}
          onOpenChange={setInfoOpen}
        />
      )}
    </>
  );
}
